// Pure builder: items[] + workflow triggers[] -> React Flow { nodes, edges }.
// Used by the docked CanvasWorkflowPanel (Phase B / C). No React, no Redux,
// no side effects.
//
// Node id conventions:
//   - InteractivePlugin sub-items: `el:<itemId>`
//   - Timeline-level scene boxes:  `tl:<boxId>` (Phase C Item 2)
//
// Source handle ids on element nodes:
//   - "next"     default forward edge between adjacent items (skipped if next is EndScreen)
//   - "correct"  / "wrong"   outcome handles for graded items, populated from
//                            workflow trigger entries (`on: "onAnswer.correct"|"onAnswer.wrong"`)
//   - "<alt.id>" one handle per BranchingQuestion alternative, edge target inferred
//                from the alternative's first showInteraction action.
//
// Timeline element nodes (Phase C Item 2): one node per non-InteractivePlugin
// scene box. Read-only placeholder cards (no editable handles) carrying the
// kind icon, the box label, and a "MM:SS → MM:SS" timing string derived from
// box.timeline.startFrame/endFrame at FPS=30 via the canonical resolveBoxTiming
// helper. An auto-edge from the timeline element with the largest
// endFrame ≤ InteractivePlugin.timeline.startFrame to the InteractivePlugin's
// first sub-item is added, labelled "in sequence".
//
// Edge label rules (Item 4):
//   - next-edges:                  "next"
//   - BranchingQuestion alt edges: the alternative's own label (e.g. "Yes", "No",
//                                  "Option A") — falls back to `alt N` only if no label is set
//   - correct/wrong trigger edges: the matching option's text suffixed with the
//                                  outcome — e.g. for MCQ with correctAnswer="b" and
//                                  options=[{key:"a",label:"Python"},{key:"b",label:"React"}]
//                                  the correct edge is labeled "React (correct)".
//                                  TrueFalse / YesNo resolve via correctAnswer (boolean) +
//                                  trueLabel/falseLabel or yesLabel/noLabel.
//                                  If the option text isn't resolvable, falls back to
//                                  plain "correct" / "wrong".

import { resolveBoxTiming } from "../../../common/resolveBoxTiming.es6";
import { formatTimeFromFrame } from "./formatTime";
import { KIND_LABEL, splitCamelCase } from "./TimelineElementNode";

const NODE_W = 220;
const NODE_H = 110;
const COL_X = 40;
const ROW_GAP = 60;
const ROW = NODE_H + ROW_GAP; // one vertical step
const TIMELINE_FPS = 30;

// pluginIds we never emit timeline nodes for: InteractivePlugin already
// produces one element node per sub-item, and sortable_* are structural
// wrappers (not authored content). Mirrors the exclusions in
// reducers/plugin_toolbars_by_id.es6 (lines 103, 115, 195).
const TIMELINE_EXCLUDED_PLUGIN_IDS = new Set([
  "InteractivePlugin",
  "sortable_container",
  "sortableBox",
]);

// Resolve the human-readable text of the option that satisfies the given outcome
// for a graded item. Returns null if no readable text can be found — callers
// then fall back to the bare outcome word.
function resolveOutcomeOptionText(item, outcome) {
  if (!item) return null;
  const kind = item.kind;
  const params = item.params || {};

  // MCQ / MultipleChoice: options=[{key,label}], correctAnswer=key.
  // Also accept the simpler shape (options=["Python","React",...], correctIndex=1)
  // since the same builder is exercised by lightweight test fixtures.
  if (kind === "MCQ" || kind === "MultipleChoice") {
    const opts = Array.isArray(params.options) ? params.options : item.options || [];
    if (!opts.length) return null;
    let correctIdx = -1;
    if (typeof params.correctAnswer !== "undefined") {
      correctIdx = opts.findIndex(
        (o) => (o && typeof o === "object" ? o.key : o) === params.correctAnswer
      );
    }
    if (correctIdx < 0 && typeof params.correctIndex === "number") {
      correctIdx = params.correctIndex;
    }
    if (correctIdx < 0 || correctIdx >= opts.length) return null;
    const correct = opts[correctIdx];
    const correctText =
      correct && typeof correct === "object" ? correct.label || correct.text : String(correct);
    if (outcome === "correct") return correctText || null;
    // wrong → any non-correct option. Prefer the first one for stability.
    for (let i = 0; i < opts.length; i++) {
      if (i === correctIdx) continue;
      const o = opts[i];
      const t = o && typeof o === "object" ? o.label || o.text : String(o);
      if (t) return t;
    }
    return null;
  }

  if (kind === "TrueFalse") {
    const trueLabel = params.trueLabel || "True";
    const falseLabel = params.falseLabel || "False";
    const correctIsTrue = params.correctAnswer === true;
    if (outcome === "correct") return correctIsTrue ? trueLabel : falseLabel;
    return correctIsTrue ? falseLabel : trueLabel;
  }

  if (kind === "YesNo") {
    const yesLabel = params.yesLabel || "Yes";
    const noLabel = params.noLabel || "No";
    const correctIsYes = params.correctAnswer === true;
    if (outcome === "correct") return correctIsYes ? yesLabel : noLabel;
    return correctIsYes ? noLabel : yesLabel;
  }

  // MultipleAnswer: correctAnswers is a set of keys; surface the first as the
  // representative "correct" text. Wrong → first non-correct option.
  if (kind === "MultipleAnswer") {
    const opts = Array.isArray(params.options) ? params.options : [];
    const correctKeys = new Set(params.correctAnswers || []);
    if (!opts.length) return null;
    if (outcome === "correct") {
      const hit = opts.find((o) => correctKeys.has(o && o.key));
      return hit ? hit.label || hit.text || null : null;
    }
    const miss = opts.find((o) => !correctKeys.has(o && o.key));
    return miss ? miss.label || miss.text || null : null;
  }

  return null;
}

function outcomeEdgeLabel(item, outcome) {
  const text = resolveOutcomeOptionText(item, outcome);
  return text ? `${text} (${outcome})` : outcome;
}

// Round 25 Item 0: resolve the human-readable label for a per-option handle
// (e.g. handle id "opt-b" on an MCQ with options=[{key:"a"},{key:"b"}] →
// returns the label of option "b"). For YesNo / TrueFalse the synthetic
// option keys "yes"/"no" or "true"/"false" map back to the kind's label
// params. Returns null when the key can't be resolved.
function resolveOptionTextByKey(item, handleId) {
  if (!item || !handleId) return null;
  const key = handleId.replace(/^opt-/, "");
  const kind = item.kind;
  const params = item.params || {};
  if (kind === "MCQ" || kind === "MultipleChoice" || kind === "MultipleAnswer") {
    const opts = Array.isArray(params.options) ? params.options : item.options || [];
    for (let i = 0; i < opts.length; i++) {
      const o = opts[i];
      const oKey =
        o && typeof o === "object" ? (o.key != null ? String(o.key) : String(i)) : String(i);
      if (oKey === key) {
        return (o && typeof o === "object" ? o.label || o.text : String(o)) || null;
      }
    }
    return null;
  }
  if (kind === "YesNo") {
    if (key === "yes") return params.yesLabel || "Yes";
    if (key === "no") return params.noLabel || "No";
    return null;
  }
  if (kind === "TrueFalse") {
    if (key === "true") return params.trueLabel || "True";
    if (key === "false") return params.falseLabel || "False";
    return null;
  }
  return null;
}

// Format frame count (FPS=30) as MM:SS for the timing chip on a timeline
// element node. Returns null when frame is null/undefined so callers can
// substitute a "scene end" marker. Shared with the canvas preview chip so
// "node label" and "Previewing at" never disagree for the same frame.
const formatFrameAsTime = formatTimeFromFrame;

// Best-effort label for a non-InteractivePlugin scene box. Boxes don't have
// an authoritative "name" field — fall back through the friendly KIND_LABEL
// map (shared with TimelineElementNode so the rendered header and the data
// layer never disagree), then a camelCase split so unknown kinds still read
// as "Hotspot Images" instead of the raw "HotspotImages" PascalCase token.
function labelForTimelineBox(box, pluginToolbar) {
  if (box && box.label) return box.label;
  if (box && box.name) return box.name;
  const pluginId = pluginToolbar && pluginToolbar.pluginId;
  if (pluginId) return KIND_LABEL[pluginId] || splitCamelCase(pluginId) || pluginId;
  return "Element";
}

export function buildElementGraph(items, triggers, options = {}) {
  const safeItems = Array.isArray(items) ? items : [];
  const safeTriggers = Array.isArray(triggers) ? triggers : [];
  const sceneBoxIds = Array.isArray(options.sceneBoxIds) ? options.sceneBoxIds : [];
  const boxesById = options.boxesById || {};
  const pluginToolbarsById = options.pluginToolbarsById || {};
  const interactiveBoxId = options.interactiveBoxId || null;
  const fps = typeof options.fps === "number" ? options.fps : TIMELINE_FPS;
  const nodes = [];
  const edges = [];

  // Round 24 Item 3: synthetic Start + End nodes auto-prepended/appended
  // when the workflow has any content (items or timeline boxes). Empty
  // inputs short-circuit to {nodes:[], edges:[]} so the panel renders a
  // quiet placeholder rather than a lone Start→End pair.
  const hasExplicitEndScreen = safeItems.some((it) => it.kind === "EndScreen");
  const hasAnyContent = safeItems.length > 0 || sceneBoxIds.length > 0;
  if (hasAnyContent) {
    nodes.push({
      id: "synthetic:start",
      type: "startNode",
      data: { label: "Start" },
      position: { x: COL_X, y: -ROW },
      draggable: false,
      selectable: false,
    });
  }

  // Pre-collect timeline boxes before wiring Start edges so we know whether
  // to route Start directly to IP sub-items or through TL nodes first.
  const collected = [];
  sceneBoxIds.forEach((boxId) => {
    const box = boxesById[boxId];
    if (!box) return;
    const toolbar = pluginToolbarsById[boxId];
    const pluginId = (toolbar && toolbar.pluginId) || null;
    if (pluginId && TIMELINE_EXCLUDED_PLUGIN_IDS.has(pluginId)) return;
    const { startFrame, endFrame } = resolveBoxTiming(box, [], fps);
    collected.push({ boxId, box, toolbar, pluginId, startFrame, endFrame });
  });
  // Sort by startFrame ASC. Stable: same-startFrame boxes preserve sceneBoxIds order.
  collected.sort((a, b) => {
    const aS = typeof a.startFrame === "number" ? a.startFrame : 0;
    const bS = typeof b.startFrame === "number" ? b.startFrame : 0;
    return aS - bS;
  });

  safeItems.forEach((item, i) => {
    nodes.push({
      id: `el:${item.id}`,
      type: "elementNode",
      data: {
        itemId: item.id,
        kind: item.kind,
        item,
        boxId: interactiveBoxId,
        allItems: safeItems,
      },
      // TL nodes occupy rows 0..numTL-1; IP Start is at row numTL.
      // Shift IP items down so they start at row (numTL + 1).
      position: { x: COL_X, y: (collected.length + 1 + i) * ROW },
    });
  });

  // Resolve InteractivePlugin box timing for the IP Start node.
  let ipStartFrame = null;
  let ipEndFrame = null;
  let ipTimingText = null;
  if (interactiveBoxId && boxesById[interactiveBoxId]) {
    const t = resolveBoxTiming(boxesById[interactiveBoxId], [], fps);
    ipStartFrame = typeof t.startFrame === "number" ? t.startFrame : null;
    ipEndFrame = typeof t.endFrame === "number" ? t.endFrame : null;
    const s = formatFrameAsTime(ipStartFrame, fps) ?? "0:00";
    const e = formatFrameAsTime(ipEndFrame, fps) ?? "end";
    ipTimingText = `${s} → ${e}`;
  }

  // Synthetic IP-level Start node — brackets the interactive sub-flow so
  // its entry point is clearly separate from the scene-level Start. The TL
  // bridge and the scene Start both wire into this node, not directly into
  // the first IP sub-item.
  if (safeItems.length > 0 && hasAnyContent) {
    nodes.push({
      id: "synthetic:ip-start",
      type: "startNode",
      data: {
        label: "Interactive",
        startFrame: ipStartFrame,
        endFrame: ipEndFrame,
        timingText: ipTimingText,
      },
      // IP Start sits between the last TL row and the first IP item row.
      position: { x: COL_X, y: collected.length * ROW },
      draggable: false,
      selectable: false,
    });
    edges.push({
      id: "e:ip-start->first-item",
      source: "synthetic:ip-start",
      target: `el:${safeItems[0].id}`,
      targetHandle: "in",
      label: "begin",
      animated: false,
    });
  }

  // Wire scene Start → IP Start when no TL boxes exist. When TL boxes are
  // present the last TL node bridges into IP Start instead (see below).
  if (hasAnyContent && safeItems.length > 0 && collected.length === 0) {
    edges.push({
      id: "e:start->ip-start",
      source: "synthetic:start",
      target: "synthetic:ip-start",
      targetHandle: "in",
      label: "begin",
      animated: false,
    });
  }

  // === Phase C Item 2: timeline element nodes ============================
  // One read-only node per non-InteractivePlugin scene box (collected +
  // sorted above). The last TL node always bridges into the first IP
  // sub-item so no timeline element floats disconnected from the graph.
  const timelineNodes = [];
  collected.forEach(({ boxId, box, toolbar, pluginId, startFrame, endFrame }, i) => {
    const startText = formatFrameAsTime(startFrame, fps);
    const endText = formatFrameAsTime(endFrame, fps);
    // "0:00 → 0:05", or "0:00 → end" when endFrame is undefined (visible to scene end).
    const timingText = `${startText == null ? "0:00" : startText} → ${endText == null ? "end" : endText}`;
    const node = {
      id: `tl:${boxId}`,
      type: "timelineElementNode",
      data: {
        boxId,
        pluginId,
        kind: pluginId,
        label: labelForTimelineBox(box, toolbar),
        startFrame: startFrame == null ? null : startFrame,
        endFrame: endFrame == null ? null : endFrame,
        timingText,
        visible: box.timeline ? box.timeline.visible !== false : true,
      },
      // Single-column layout: TL nodes share COL_X and occupy rows 0..i.
      position: { x: COL_X, y: i * ROW },
      draggable: true,
      selectable: true,
    };
    timelineNodes.push(node);
    nodes.push(node);
  });
  // Round 24 Item 4: auto-edge each consecutive pair of timeline nodes
  // (now in startFrame-sorted order) so the chain reads as a sequence.
  for (let i = 0; i < timelineNodes.length - 1; i++) {
    const a = timelineNodes[i];
    const b = timelineNodes[i + 1];
    edges.push({
      id: `e:tl-next:${a.data.boxId}->${b.data.boxId}`,
      source: a.id,
      target: b.id,
      targetHandle: "in",
      label: "next",
      animated: false,
    });
  }

  // Bridge: last TL node → Interactive Start. Routes through the IP Start
  // node so the two graph sections (timeline visual / interactive) always
  // have a clean handoff at the IP Start marker.
  if (safeItems.length > 0 && timelineNodes.length > 0) {
    const lastTl = timelineNodes[timelineNodes.length - 1];
    edges.push({
      id: `e:in-sequence:${lastTl.data.boxId}->ip-start`,
      source: lastTl.id,
      target: "synthetic:ip-start",
      targetHandle: "in",
      label: "in sequence",
      animated: false,
    });
  }

  // Graded item kinds that expose correct/wrong handles (ElementNode renders
  // these handles on their cards). When no explicit answer triggers exist for
  // a graded item, auto-route BOTH correct and wrong handles to the next item
  // so authors immediately see that both paths converge — the "intersection"
  // is made explicit without any manual trigger authoring.
  const GRADED_KINDS = new Set(["MCQ", "MultipleChoice", "TrueFalse", "YesNo", "MultipleAnswer"]);
  // Visual overlay kinds — appear/disappear based on showWhen; excluded from
  // sequential routing edges. VisualImage with showWhen "correct"/"wrong" gets
  // a dedicated showWhen edge drawn separately below.
  const VISUAL_KINDS = new Set(["VisualLabel", "VisualImage"]);

  // Default next-edges between consecutive items (EndScreen is terminal — no inbound default edge).
  // Visual overlay items are excluded from sequential routing (they are not navigated to/from).
  for (let i = 0; i < safeItems.length - 1; i++) {
    const a = safeItems[i];
    const b = safeItems[i + 1];
    if (b.kind === "EndScreen") continue;
    if (VISUAL_KINDS.has(a.kind) || VISUAL_KINDS.has(b.kind)) continue;

    if (GRADED_KINDS.has(a.kind)) {
      // Check if explicit onAnswer triggers already cover this source item.
      const hasCorrectTrigger = safeTriggers.some(
        (tr) =>
          (tr.sourceItemId === a.id || tr.pluginBoxId === a.id) && tr.on === "onAnswer.correct"
      );
      const hasWrongTrigger = safeTriggers.some(
        (tr) => (tr.sourceItemId === a.id || tr.pluginBoxId === a.id) && tr.on === "onAnswer.wrong"
      );
      // Only emit auto-answer edges when neither handle is already wired
      // by an explicit trigger — explicit triggers take precedence.
      if (!hasCorrectTrigger) {
        edges.push({
          id: `e:correct-auto:${a.id}->${b.id}`,
          source: `el:${a.id}`,
          sourceHandle: "correct",
          target: `el:${b.id}`,
          targetHandle: "in",
          label: "correct",
          animated: false,
        });
      }
      if (!hasWrongTrigger) {
        edges.push({
          id: `e:wrong-auto:${a.id}->${b.id}`,
          source: `el:${a.id}`,
          sourceHandle: "wrong",
          target: `el:${b.id}`,
          targetHandle: "in",
          label: "wrong",
          animated: false,
        });
      }
    } else {
      edges.push({
        id: `e:next:${a.id}->${b.id}`,
        source: `el:${a.id}`,
        sourceHandle: "next",
        target: `el:${b.id}`,
        targetHandle: "in",
        label: "next",
        animated: false,
      });
    }
  }

  // "Show on answer" edges — drawn from graded items' outcome actions (new format)
  // and legacy onCorrectShowItemId / onWrongShowItemId fields (backward compat).
  safeItems.forEach((item) => {
    const p = item.params || {};

    // Collect showItem targets from action arrays (new format)
    const correctShowIds = new Set();
    const wrongShowIds = new Set();
    (p.onCorrectActions || []).forEach((a) => {
      if (a && a.kind === "showItem" && a.itemId) correctShowIds.add(a.itemId);
    });
    (p.onWrongActions || []).forEach((a) => {
      if (a && a.kind === "showItem" && a.itemId) wrongShowIds.add(a.itemId);
    });
    // Legacy single-id fields
    if (p.onCorrectShowItemId || item.onCorrectShowItemId)
      correctShowIds.add(p.onCorrectShowItemId || item.onCorrectShowItemId);
    if (p.onWrongShowItemId || item.onWrongShowItemId)
      wrongShowIds.add(p.onWrongShowItemId || item.onWrongShowItemId);

    correctShowIds.forEach((correctId) => {
      edges.push({
        id: `e:show:correct:${item.id}->${correctId}`,
        source: `el:${item.id}`,
        sourceHandle: "correct",
        target: `el:${correctId}`,
        targetHandle: "in",
        label: "✓ göster",
        animated: false,
        data: { showBinding: "correct" },
      });
    });
    wrongShowIds.forEach((wrongId) => {
      edges.push({
        id: `e:show:wrong:${item.id}->${wrongId}`,
        source: `el:${item.id}`,
        sourceHandle: "wrong",
        target: `el:${wrongId}`,
        targetHandle: "in",
        label: "✗ göster",
        animated: false,
        data: { showBinding: "wrong" },
      });
    });
  });

  // BranchingQuestion alternative edges — resolve target by matching action.interactionKind to a sibling item kind.
  safeItems.forEach((item) => {
    if (item.kind !== "BranchingQuestion") return;
    // BQ items can arrive with alternatives at params.alternatives (canonical
    // shape produced by defaultParamsFor + InteractivePluginRenderer) OR at the
    // top level of the item (seedInteractivePluginBoxAndAddItem auto-create
    // flow spreads defaultParamsFor at the top level). Read both so edges
    // render regardless of the path that produced the item.
    const alts = (item.params && item.params.alternatives) || item.alternatives || [];
    alts.forEach((alt, ai) => {
      const action = (alt.actions || [])[0];
      if (!action) return;
      let targetItemId = null;
      if (action.kind === "showInteraction" && action.interactionKind) {
        const targetItem = safeItems.find((it) => it.kind === action.interactionKind);
        targetItemId = targetItem ? targetItem.id : null;
      }
      if (!targetItemId) return;
      edges.push({
        id: `e:alt:${item.id}:${alt.id}->${targetItemId}`,
        source: `el:${item.id}`,
        sourceHandle: alt.id,
        target: `el:${targetItemId}`,
        targetHandle: "in",
        // Per Item 4: edge label is the alternative's own author-supplied text
        // (e.g. "Yes", "Option A"). Generic "alt N" is only a safety fallback.
        label: alt.label || `alt ${ai + 1}`,
        // BranchingQuestion alt edges are synthesized from the alternative's
        // first showInteraction action, not from a workflow.triggers entry —
        // there is no triggerId to delete, so the trashcan overlay does not
        // render on these edges. Authors edit alternatives via the
        // BranchingQuestion item editor.
      });
    });
  });

  // onCorrect / onWrong trigger edges — section.workflow.triggers carry the routing.
  safeTriggers.forEach((tr) => {
    if (!tr.on || !tr.on.startsWith("onAnswer.")) return;
    const sourceItem = safeItems.find(
      (it) => it.id === tr.sourceItemId || it.id === tr.pluginBoxId
    );
    if (!sourceItem) return;
    const targetItemId = tr.actionTargetItemId;
    const target = safeItems.find((it) => it.id === targetItemId);
    if (!target) return;
    const outcome = tr.on.split(".")[1]; // "correct" | "wrong"
    edges.push({
      id: `e:${outcome}:${sourceItem.id}->${target.id}`,
      source: `el:${sourceItem.id}`,
      sourceHandle: outcome,
      target: `el:${target.id}`,
      targetHandle: "in",
      // Per Item 4: include the matching option's text when resolvable, e.g.
      // "React (correct)". Falls back to plain outcome word when the option text
      // is not available.
      label: outcomeEdgeLabel(sourceItem, outcome),
      // Round 25 Item 0: carry the source trigger id so the custom
      // DeletableEdge can render a trashcan that dispatches
      // deleteWorkflowTrigger(sectionId, triggerId) for this specific edge.
      // Synthetic Start/End edges, default next-edges, and timeline auto-edges
      // intentionally omit data.triggerId so their trashcan is suppressed.
      data: { triggerId: tr.id },
    });
  });

  // Round 25 Item 0: onOptionPicked trigger edges — per-option outflows for
  // MCQ / MultipleChoice / MultipleAnswer / YesNo / TrueFalse. The source
  // handle is the trigger's optionKey (e.g. "opt-b") which matches the
  // handle id ElementNode renders for that option. Edge label is the
  // resolved option text so authors see which answer routes where.
  safeTriggers.forEach((tr) => {
    if (tr.on !== "onOptionPicked") return;
    const sourceItem = safeItems.find(
      (it) => it.id === tr.sourceItemId || it.id === tr.pluginBoxId
    );
    if (!sourceItem) return;
    const target = safeItems.find((it) => it.id === tr.actionTargetItemId);
    if (!target) return;
    const optionKey = tr.optionKey;
    if (!optionKey) return;
    edges.push({
      id: `e:opt:${sourceItem.id}:${optionKey}->${target.id}`,
      source: `el:${sourceItem.id}`,
      sourceHandle: optionKey,
      target: `el:${target.id}`,
      targetHandle: "in",
      label: resolveOptionTextByKey(sourceItem, optionKey) || optionKey,
      data: { triggerId: tr.id },
    });
  });

  // Round 24 Item 3: synthetic End node + completion edges (only when the
  // workflow has any content — empty inputs short-circuit above).
  // The End node is "satisfied" (visual: green check) when an explicit
  // EndScreen sub-item exists in the InteractivePlugin items[] — the
  // synthetic End is then a courtesy marker. Without an EndScreen item
  // the End node renders with a warning accent so authors notice the
  // missing exit.
  if (hasAnyContent) {
    const lastTimeline = timelineNodes[timelineNodes.length - 1];
    const lastSubItem = safeItems.length > 0 ? safeItems[safeItems.length - 1] : null;
    // Single-column layout: all rows stack in order.
    // IP items start at row (numTL + 1); IP End is at row (numTL + 1 + numIP).
    // Scene End is one row below whatever ends last.
    const bottomRow =
      safeItems.length > 0
        ? timelineNodes.length + 1 + safeItems.length + 1 // +1 IP Start, +1 IP End
        : timelineNodes.length;
    const endY = bottomRow * ROW + 40;
    nodes.push({
      id: "synthetic:end",
      type: "endNode",
      data: {
        label: "End",
        satisfied: hasExplicitEndScreen,
      },
      position: { x: COL_X, y: endY + 40 },
      draggable: false,
      selectable: false,
    });
    if (lastSubItem) {
      // Synthetic IP-level End node — brackets the interactive sub-flow exit.
      // Row: numTL + 1 (IP Start) + numIP (IP items) = timelineNodes.length + 1 + safeItems.length
      const ipEndY = (timelineNodes.length + 1 + safeItems.length) * ROW + 20;
      nodes.push({
        id: "synthetic:ip-end",
        type: "endNode",
        data: { label: "Interactive End", satisfied: hasExplicitEndScreen },
        position: { x: COL_X, y: ipEndY },
        draggable: false,
        selectable: false,
      });
      edges.push({
        id: "e:last-item->ip-end",
        source: `el:${lastSubItem.id}`,
        target: "synthetic:ip-end",
        targetHandle: "in",
        label: "end",
        animated: false,
      });
      // IP End → Scene End: interactive sub-flow exits into the scene's End.
      edges.push({
        id: "e:ip-end->end",
        source: "synthetic:ip-end",
        target: "synthetic:end",
        targetHandle: "in",
        label: "end",
        animated: false,
      });
    }
    // Timeline visual nodes connect directly to scene End — the visual spans
    // the whole scene and terminates at scene end, creating a parallel branch
    // alongside (or independent of) the interactive sub-flow.
    if (lastTimeline) {
      edges.push({
        id: "e:last-tl->end",
        source: lastTimeline.id,
        target: "synthetic:end",
        targetHandle: "in",
        label: "end",
        animated: false,
      });
    }
    if (timelineNodes.length > 0) {
      edges.push({
        id: "e:start->first-tl",
        source: "synthetic:start",
        target: timelineNodes[0].id,
        targetHandle: "in",
        label: "begin",
        animated: false,
      });
    }
  }

  return { nodes, edges };
}
